// Shared video embed helpers for the Flexible Content Card and the Video
// Recommendations widget. An editor pastes a YouTube or Vimeo URL into the
// same `videoUrl` field (SH-496); both providers are embedded through their
// privacy-friendly variant — the `youtube-nocookie` host and Vimeo's `dnt=1`.

export type VideoProvider = 'youtube' | 'vimeo';

export interface VideoEmbed {
  provider: VideoProvider;
  /** Base player URL, provider privacy parameters included. */
  src: string;
}

// Anchored to the host, so a YouTube-like substring inside another URL (e.g.
// `?u=youtu.be/x`) is not mistaken for a video. The scheme stays optional, as
// before.
const YOUTUBE_WATCH =
  /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/watch\?(?:[^#]*&)?v=|youtu\.be\/)([\w-]+)/i;
const YOUTUBE_EMBED = /^https?:\/\/www\.youtube(?:-nocookie)?\.com\/embed\//;
// `vimeo.com/{id}` and the unlisted share link `vimeo.com/{id}/{hash}`.
const VIMEO_PAGE =
  /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:\/([\da-z]+))?\/?(?:[?#].*)?$/i;
// The URL Vimeo's "Share → Embed" dialog puts into its iframe snippet.
const VIMEO_PLAYER =
  /^https?:\/\/player\.vimeo\.com\/video\/(\d+)\/?(?:[?#].*)?$/i;

const YOUTUBE_ORIGIN = 'https://www.youtube-nocookie.com';
const VIMEO_ORIGIN = 'https://player.vimeo.com';

function vimeoEmbed(id: string, hash: string | null): VideoEmbed {
  const src = new URL(`${VIMEO_ORIGIN}/video/${id}`);
  // Unlisted videos only play with their hash; every other pasted parameter
  // (`badge`, `app_id`, …) is dropped so Vimeo's defaults apply.
  if (hash) {
    src.searchParams.set('h', hash);
  }
  src.searchParams.set('dnt', '1');
  return { provider: 'vimeo', src: src.toString() };
}

/** null when the URL is neither YouTube nor Vimeo. */
export function parseVideoUrl(url: string): VideoEmbed | null {
  const trimmed = url.trim();

  const watchMatch = trimmed.match(YOUTUBE_WATCH);
  if (watchMatch) {
    return {
      provider: 'youtube',
      src: `${YOUTUBE_ORIGIN}/embed/${watchMatch[1]}`,
    };
  }
  if (YOUTUBE_EMBED.test(trimmed)) {
    return {
      provider: 'youtube',
      src: trimmed.replace(
        'www.youtube.com/embed/',
        'www.youtube-nocookie.com/embed/',
      ),
    };
  }

  const vimeoPage = trimmed.match(VIMEO_PAGE);
  if (vimeoPage) {
    return vimeoEmbed(vimeoPage[1], vimeoPage[2] ?? null);
  }
  const vimeoPlayer = trimmed.match(VIMEO_PLAYER);
  if (vimeoPlayer) {
    return vimeoEmbed(vimeoPlayer[1], new URL(trimmed).searchParams.get('h'));
  }

  return null;
}

/**
 * Normalises any YouTube or Vimeo URL an editor may paste into an embed URL.
 * `controllable` adds what the provider needs for `postPlayerCommand`
 * (YouTube's `enablejsapi=1`; Vimeo's player listens without it). Anything
 * else is returned unchanged so a broken link stays visible rather than
 * silently turning into a wrong video.
 */
export function toEmbedUrl(
  url: string,
  opts: { autoplay?: boolean; controllable?: boolean } = {},
): string {
  const embed = parseVideoUrl(url);
  if (!embed) {
    return url;
  }
  const src = new URL(embed.src);
  if (opts.controllable && embed.provider === 'youtube') {
    src.searchParams.set('enablejsapi', '1');
  }
  if (opts.autoplay) {
    src.searchParams.set('autoplay', '1');
  }
  return src.toString();
}

/** Sends play/pause to the iframe in the provider's postMessage dialect. */
export function postPlayerCommand(
  iframe: HTMLIFrameElement | null,
  provider: VideoProvider,
  command: 'play' | 'pause',
): void {
  const target = iframe?.contentWindow;
  if (!target) {
    return;
  }
  if (provider === 'vimeo') {
    target.postMessage(JSON.stringify({ method: command }), VIMEO_ORIGIN);
    return;
  }
  target.postMessage(
    JSON.stringify({
      event: 'command',
      func: command === 'play' ? 'playVideo' : 'pauseVideo',
      args: [],
    }),
    // `parseVideoUrl` always embeds YouTube from this host.
    YOUTUBE_ORIGIN,
  );
}
